import {
  IRolesListResponse,
  IModuleWithPermissions,
} from "@/types/roles-permissions";
import ApiClient from "@/utils/ApiClient";

const getRequiredHeaders = () => {
  return {
    "x-lang": "en",
    "x-session-id": localStorage.getItem("sessionId") || "",
    "x-location-name": localStorage.getItem("locationName") || "",
    "x-latitude": localStorage.getItem("latitude") || "",
    "x-longitude": localStorage.getItem("longitude") || "",
  };
};

export const RolesService = {
  getRoles: async (
    page = 1,
    pageSize = 10,
    search?: string,
  ): Promise<IRolesListResponse> => {
    const params: { page: number; limit: number; search?: string } = {
      page,
      limit: pageSize,
    };
    if (search && search.trim()) {
      params.search = search.trim();
    }
    const res = await ApiClient.get("/roles", {
      params,
    });

    if (Array.isArray(res.data)) {
      return {
        roles: res.data,
        totalCount: res.data.length,
      };
    } else if (res.data.status && res.data.data) {
      if (Array.isArray(res.data.data)) {
        return {
          roles: res.data.data,
          totalCount: res.data.data.length,
        };
      } else if (res.data.data.roles && Array.isArray(res.data.data.roles)) {
        return {
          roles: res.data.data.roles,
          totalCount:
            res.data.data.totalCount ||
            res.data.data.totalItems ||
            res.data.data.roles.length,
        };
      } else {
        return {
          roles: [],
          totalCount: 0,
        };
      }
    }
    // Fallback
    else {
      return {
        roles: [],
        totalCount: 0,
      };
    }
  },

  getRoleWithPermissions: async (id: string) => {
    try {
      const response = await ApiClient.get(`/roles/permissions/${id}`, {
        headers: getRequiredHeaders(),
      });

      let modulePermissions: IModuleWithPermissions[] = [];

      if (
        response.data &&
        response.data.status === true &&
        response.data.data &&
        response.data.data.length > 0
      ) {
        modulePermissions = response.data.data.map((module: unknown) => {
          const mod = module as {
            id: string;
            name: string;
            description?: string;
            module_key?: string;
            key?: string;
            permissions: unknown[];
          };
          const permissionsMap: { [key: string]: boolean } = {};

          if (Array.isArray(mod.permissions)) {
            mod.permissions.forEach((permission: unknown) => {
              const perm = permission as {
                permission_type?: string;
                key?: string;
                id: string | number;
                is_granted?: boolean;
                role_has_permissions?: unknown[];
              };
              const permKey = (perm.permission_type ||
                perm.key ||
                (typeof perm.id === "string"
                  ? perm.id.split("_").pop()
                  : "view")) as string;

              permissionsMap[permKey] = perm.is_granted === true;
            });
          }

          // Check if we have raw permissions format
          const hasRawPermissions =
            Array.isArray(mod.permissions) &&
            mod.permissions.length > 0 &&
            mod.permissions[0] &&
            typeof mod.permissions[0] === "object" &&
            ("role_has_permissions" in mod.permissions[0] ||
              "id" in mod.permissions[0]);

          return {
            module: {
              id: mod.id,
              name: mod.name,
              description: mod.description || "",
              moduleKey: mod.module_key || mod.key || mod.id,
            },
            permissions: hasRawPermissions ? {} : permissionsMap,
            rawPermissions: hasRawPermissions ? mod.permissions : undefined,
          };
        });
      }

      return {
        modulePermissions,
      };
    } catch (error) {
      console.error(`Error fetching permissions for role ID ${id}:`, error);
      throw error;
    }
  },

  getAllPermissions: async (page = 1, limit = 1000) => {
    try {
      const response = await ApiClient.get("/permissions", {
        params: { page, limit },
        headers: getRequiredHeaders(),
      });

      let modulePermissions: IModuleWithPermissions[] = [];

      if (
        response.data &&
        response.data.status === true &&
        response.data.data &&
        response.data.data.permissions &&
        Array.isArray(response.data.data.permissions)
      ) {
        modulePermissions = response.data.data.permissions.map(
          (module: unknown) => {
            const mod = module as {
              id: string;
              name: string;
              permissions: unknown[];
            };
            const permissionsMap: { [key: string]: boolean } = {};

            if (Array.isArray(mod.permissions)) {
              mod.permissions.forEach((permission: unknown) => {
                const perm = permission as { id: string };
                permissionsMap[perm.id] = false; // Default to false for all permissions
              });
            }

            // Check if we have raw permissions format
            const hasRawPermissions =
              Array.isArray(mod.permissions) &&
              mod.permissions.length > 0 &&
              mod.permissions[0] &&
              typeof mod.permissions[0] === "object" &&
              "id" in mod.permissions[0];

            return {
              module: {
                id: mod.id,
                name: mod.name,
                description: "",
                moduleKey: mod.id,
              },
              permissions: hasRawPermissions ? {} : permissionsMap,
              rawPermissions: hasRawPermissions
                ? mod.permissions.map((perm: unknown) => ({
                    ...(perm as Record<string, unknown>),
                    role_has_permissions: [], // Initialize as unchecked
                  }))
                : undefined,
            };
          },
        );
      }

      return {
        modulePermissions,
      };
    } catch (error) {
      console.error("Error fetching all permissions:", error);
      throw error;
    }
  },

  saveRolePermissions: async (
    roleId: string,
    permissions: Array<{
      moduleId: string;
      permissionId: string;
      isGranted: boolean;
    }>,
  ) => {
    try {
      const permissionIds = permissions
        .filter((perm) => perm.isGranted)
        .map((perm) => perm.permissionId);

      const response = await ApiClient.patch(
        `/roles/permissions/${roleId}`,
        { ids: permissionIds },
        { headers: getRequiredHeaders() },
      );

      return response.data;
    } catch (error) {
      console.error(`Error saving permissions for role ID ${roleId}:`, error);
      throw error;
    }
  },

  createRole: async (roleData: {
    name: string;
    has_admin_panel_access: boolean;
    has_user_front_access: boolean;
    description?: string;
  }) => {
    try {
      const response = await ApiClient.post("/roles", roleData, {
        headers: getRequiredHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error("Error creating role:", error);
      throw error;
    }
  },

  updateRole: async (
    roleId: string,
    roleData: {
      name: string;
      has_admin_panel_access: boolean;
      has_user_front_access: boolean;
      description?: string;
    },
  ) => {
    try {
      const response = await ApiClient.patch(`/roles/${roleId}`, roleData, {
        headers: getRequiredHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error(`Error updating role ID ${roleId}:`, error);
      throw error;
    }
  },

  deleteRole: async (roleId: string) => {
    try {
      const response = await ApiClient.delete(`/roles/${roleId}`, {
        headers: getRequiredHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error(`Error deleting role ID ${roleId}:`, error);
      throw error;
    }
  },

  getAllRoles: async (): Promise<IRolesListResponse> => {
    try {
      // Fetch all roles with a large page size to get all at once
      return await RolesService.getRoles(1, 1000);
    } catch (error) {
      console.error("Error fetching all roles:", error);
      throw error;
    }
  },

  getRoleOptions: async (): Promise<
    Array<{ value: string; label: string }>
  > => {
    try {
      const response = await RolesService.getAllRoles();
      return response.roles.map((role) => ({
        value: role.name,
        label: role.display_name || role.name,
      }));
    } catch (error) {
      console.error("Error fetching role options:", error);
      throw error;
    }
  },
};
